import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { syncPushIfGranted } from "@/lib/push";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, obj = logged in
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem("impersonator_token"));

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => { setUser(res.data.user); syncPushIfGranted(); })
      .catch(() => {
        localStorage.removeItem("token");
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    sessionStorage.setItem("showScripture", "1");
    setUser(data.user);
    syncPushIfGranted();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { window.dispatchEvent(new Event("iihr:stop-playback")); } catch (e) {}
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    localStorage.removeItem("token");
    localStorage.removeItem("impersonator_token");
    setImpersonating(false);
    setUser(false);
  };

  const impersonate = async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    const adminToken = localStorage.getItem("token");
    if (adminToken) localStorage.setItem("impersonator_token", adminToken);
    localStorage.setItem("token", data.token);
    try { window.dispatchEvent(new Event("iihr:stop-playback")); } catch (e) {}
    setImpersonating(true);
    setUser(data.user);
    return data.user;
  };

  const stopImpersonating = async () => {
    const adminToken = localStorage.getItem("impersonator_token");
    if (!adminToken) return;
    localStorage.setItem("token", adminToken);
    localStorage.removeItem("impersonator_token");
    setImpersonating(false);
    try { window.dispatchEvent(new Event("iihr:stop-playback")); } catch (e) {}
    try {
      const res = await api.get("/auth/me");
      setUser(res.data.user);
    } catch (e) {
      localStorage.removeItem("token");
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, impersonate, stopImpersonating, impersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
